import React from 'react'
import { withTranslation } from 'react-i18next'
import useSWR from 'swr'
import dayjs from 'dayjs'
import * as UIcons from '@iconscout/react-unicons'
import BreadCrumb from 'components/BreadCrumb'
import StatusPill from '../../shared/StatusPill'
import RoleHeaderCard from './RoleHeaderCard'
import { PERMISSION_CATEGORIES } from '../permissionCategories'

function ViewAllBtn({ onClick, className = 'px-4 py-1.5' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1.5 shrink-0 bg-white border border-border-lines rounded-full text-sm font-semibold text-dark-blue whitespace-nowrap ${className}`}
    >
      View all <UIcons.UilArrowRight size={16} />
    </button>
  )
}

function Card({ icon: Icon, title, subtitle, action, children }) {
  return (
    <div className="w-full bg-white rounded-2xl border border-border-lines shadow-sm p-6 flex flex-col gap-4 mb-5">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          {Icon && <Icon size={24} className="text-secondary shrink-0" />}
          <div>
            <p className="text-xl font-semibold text-secondary">{title}</p>
            {subtitle && <p className="text-xs text-secondary mt-0.5">{subtitle}</p>}
          </div>
        </div>
        {action}
      </div>

      {children}
    </div>
  )
}

function InfoRow({ label, value }) {
  if (!value) return null

  return (
    <div className="flex items-start gap-4 py-2">
      <p className="w-full max-w-[220px] shrink-0 text-secondary">{label}</p>
      <p className="flex-1 text-dark-blue">{value}</p>
    </div>
  )
}

export default withTranslation()(function RoleDetail({ t, role, onEdit, onDuplicate, onToggleStatus, onViewPermissions }) {
  const { data: usersRes } = useSWR('user-mgmt/users')
  const { data: branchesRes } = useSWR('merchants/branches')

  const users = usersRes?.data?.data ?? []
  const branches = branchesRes?.data ?? []
  const branchNameById = branches.reduce((acc, item) => ({ ...acc, [item.id]: item.name }), {})
  const assignedUsers = users.filter((user) => user.roles?.some((r) => r.id === role.id))

  const permissions = role.permissions ?? []
  const totalPermissions = PERMISSION_CATEGORIES.reduce((sum, c) => sum + c.permissions.length, 0)

  return (
    <section className="w-full max-w-4xl mx-auto">
      <div className="mb-5">
        <BreadCrumb
          items={[
            { name: t('settings.hub.title'), url: '/settings' },
            { name: t('settings.hub.team.title'), url: '/settings/team-access' },
            { name: t('settings.role_management.page_title'), url: '/settings/role-management' },
            { name: t('settings.role_management.detail.page_title') }
          ]}
        />
      </div>

      <RoleHeaderCard
        role={role}
        branchName={branchNameById[role.branch_id]}
        usersAssigned={role.users_count ?? assignedUsers.length}
        onEdit={onEdit}
        onDuplicate={onDuplicate}
        onToggleStatus={onToggleStatus}
      />

      <Card icon={UIcons.UilInfoCircle} title={t('settings.role_management.detail.role_information')}>
        <div className="flex flex-col">
          <InfoRow label={t('settings.role_management.detail.role_name')} value={role.display_name ?? role.name} />
          <InfoRow label={t('settings.role_management.detail.role_description')} value={role.description} />
          <InfoRow label={t('settings.role_management.detail.date_created')} value={role.created_at ? dayjs(role.created_at).format('MMM D, YYYY h:mm A') : null} />
          <InfoRow label={t('settings.role_management.detail.created_by')} value={role.created_by} />
          <InfoRow label={t('settings.role_management.detail.last_updated')} value={role.updated_at ? dayjs(role.updated_at).format('MMM D, YYYY h:mm A') : null} />
          <InfoRow label={t('settings.role_management.detail.notes')} value={role.notes} />
        </div>
      </Card>

      <Card icon={UIcons.UilLock} title={t('settings.role_management.detail.access_scope')}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <p className="text-xs font-semibold text-secondary mb-1">{t('settings.role_management.detail.branch_access')}</p>
            <p className="text-base text-dark-blue">{branchNameById[role.branch_id] ?? '—'}</p>
          </div>
          <div>
            <p className="text-xs font-semibold text-secondary mb-1">{t('settings.role_management.detail.role_level')}</p>
            <p className="text-base text-dark-blue">{role.role_level ? t(`settings.role_management.form.role_level.options.${role.role_level}`) : '—'}</p>
          </div>
        </div>
      </Card>

      <Card
        icon={UIcons.UilLock}
        title={t('settings.role_management.detail.assigned_permissions')}
        subtitle={t('settings.role_management.detail.permissions_assigned', { count: permissions.length, total: totalPermissions })}
        action={<ViewAllBtn onClick={onViewPermissions} />}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {
            PERMISSION_CATEGORIES.map((category) => {
              const assigned = category.permissions.filter((p) => permissions.includes(p)).length
              return (
                <div key={category.key}>
                  <p className="text-base text-dark-blue">{t(`settings.role_management.permissions_step.categories.${category.key}`)}</p>
                  <p className="text-xs text-secondary">{`${assigned}/${category.permissions.length} permissions assigned`}</p>
                </div>
              )
            })
          }
        </div>
      </Card>

      <Card
        icon={UIcons.UilUsersAlt}
        title={t('settings.role_management.detail.assigned_users')}
        action={<ViewAllBtn className="px-3 py-1" />}
      >
        <div className="overflow-x-auto rounded-lg border border-border-lines">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-dark-blue bg-background">
                <th className="p-4 font-semibold">{t('settings.user_management.columns.user')}</th>
                <th className="p-4 font-semibold">{t('settings.user_management.columns.email')}</th>
                <th className="p-4 font-semibold">{t('settings.user_management.columns.branch')}</th>
                <th className="p-4 font-semibold">{t('misc.status')}</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-border-lines">
              {
                assignedUsers.length ? assignedUsers.slice(0, 4).map((user) => (
                  <tr key={user.id}>
                    <td className="p-4 text-dark-blue">{`${user.first_name ?? ''} ${user.last_name ?? ''}`}</td>
                    <td className="p-4 text-dark-blue">{user.email}</td>
                    <td className="p-4 text-dark-blue">{branchNameById[user.branch_id] ?? '—'}</td>
                    <td className="p-4"><StatusPill active={(user.status ?? 'active') !== 'inactive'} /></td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={4} className="text-center py-8 text-secondary">—</td>
                  </tr>
                )
              }
            </tbody>
          </table>
        </div>
      </Card>
    </section>
  )
})
